// Tab shell — 4 tabs (家/攝影機/事件/帳號), equal weight, no centre-emphasis. Left rail
// ≥768px, fixed bottom tab bar below (global.css .app-nav).
// 三平台同形（APP#145 業主 2026-09-03 裁決 (a)；specs/16 §3.1 四-Tab、APP#101 A「Live 非 Tab」）：
// 順序／標籤／圖示對齊 iOS `HomeTabs.swift`、Android `MainActivity.kt` `tabs`。
// - 多機分割：從「攝影機」頁 toolbar 進 `/live/grid`（兩台以上在線才出現），`/live` 中繼頁已刪
//   （舊連結 → redirect `/devices`）。
// - 訂閱：收在帳號頁內（`19` §66），不設頂層 tab；入口依 brand cap `subscription:manage` 條件渲染。
// - 回顧 `/memories`：兩端無此面，web 保留頁面、入口併入事件頁（`ia.md` §9 GAP-HOME-WEB-TABS 已收斂）。
//
// Events tab 未讀紅點（16 §2.2，W3/APP-01）：來源 = WSS room 廣播（web v1 無
// push，16 §10）。不在 /events 時收到即時事件 → bump；進入 /events 即清零。
// 已讀狀態 v1 本地（16 §5.3）。
// WEB3-F3：room 訂閱 tab 無關——連線由 AuthContext 隨登入建立，但停留 /account
// 之類非裝置頁時若無人宣告 rooms，badge 仍收不到廣播。shell 掛載（= 已登入，
// RequireAuth 之內）即拉一次裝置清單宣告全部 rooms（16 §5.2/§2.2）；liveEvents
// registry 負責維持與重連重訂（冪等），裝置頁既有 ensureEventRooms 呼叫不變。
//
// 連線狀態 banner（W2-02，spec 10 §4.3/§7/AC-OS-8）：degraded（WSS 斷、REST
// 正常）→ 黃 banner；network down → 灰 banner。banner 全域唯一落點在這。
// degraded 時「即時功能 disable」指 start_viewing 類動作入口（裝置卡 ▶、多路同看），
// tab 本身不受影響。

import { brandDisplayName, brandLogoSrc } from '../lib/brand';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ensureEventRooms, onLiveEvent, unreadEvents } from '../features/events/liveEvents';
import { purgeExpiredSnapshots } from '../features/events/eventSnapshotDb';
import { doorbellRing, isDoorbellEvent } from '../features/doorbell/doorbellRing';
import { IncomingDoorbellCard } from '../features/doorbell/IncomingDoorbellCard';
import { doorbellAnswered } from '../features/doorbell/doorbellAnswered';
import { signaling } from '../lib/ws';
import { isSnoozed } from '../features/devices/snooze';
import { apiFetch, getPreferences } from '../lib/api';
import { adoptAccountTheme } from '../theme';
import { ConnectionBanner } from './ConnectionBanner';
import { PanelSosBanners, PanelSosOverlay, usePanelSosPolling } from '../features/devices/PanelSosAlert';
import { PanelCallOverlay, usePanelCalls } from '../features/devices/PanelCallOverlay';
import { PanelCalNotices, usePanelCalNoticePolling } from '../features/devices/PanelCalNotices';
import { NavIcon, type NavIconName } from './NavIcon';

// 事件 ⇄ 裝置「並重」雙主軸（apps/home/specs/ia.md §0，2026-06-17）：「家」landing 對等
// 呈現兩軸；事件與攝影機在導覽中比鄰、等權（事件不再次於裝置）。
// ⚠️ 固定四項，順序 = iOS/Android。要加 Tab 先改 `16` §3.1 並三平台同動。
const TABS: Array<{ to: string; labelKey: string; icon: NavIconName }> = [
  { to: '/home', labelKey: 'nav.home', icon: 'home' },
  { to: '/devices', labelKey: 'nav.devices', icon: 'devices' },
  { to: '/events', labelKey: 'nav.events', icon: 'events' },
  { to: '/account', labelKey: 'nav.account', icon: 'account' },
];

// ls-loop：帳號 VMS（MV，F28）的入口。VMS 頁面原本只能從各頁的小連結進去，側欄看不到 → 桌面側欄四個分頁下面加一區；
// 手機底欄不放（空間只夠四格，VMS 是電腦上用的）。拆不拆成獨立網站，John 看過效果再決定（2026-10-11）。
const VMS_LINKS: Array<{ to: string; labelKey: string }> = [
  { to: '/vms/devices', labelKey: 'deviceOverview.title' },
  { to: '/playback/multi', labelKey: 'multiPlayback.title' },
  { to: '/vms/export', labelKey: 'vmsExport.title' },
  { to: '/vms/activity', labelKey: 'vmsActivity.title' },
];

export function AppShell() {
  const { t } = useTranslation();
  const unread = useSyncExternalStore(unreadEvents.subscribe, unreadEvents.get);
  const { pathname } = useLocation();
  const onEventsTab = pathname.startsWith('/events');
  const onEventsRef = useRef(onEventsTab);
  onEventsRef.current = onEventsTab;

  // 外觀存在帳號上（帳號 → 外觀）：登入後套用一次，換電腦 / 瀏覽器也一樣
  useEffect(() => {
    getPreferences()
      .then((p) => adoptAccountTheme(p.theme))
      .catch(() => undefined);
  }, []);

  // 事件縮圖 90 天自刪:認證進 app(AppShell mount)就清一次,分頁重新可見(≈前景)再清一次
  // ——不依賴使用者是否開事件頁(大部分人不會開)。事件視圖 mount 那次仍保留當「看事件時的即時清理」。
  useEffect(() => {
    void purgeExpiredSnapshots();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void purgeExpiredSnapshots();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // deviceId → display_name（門鈴卡顯示用;同一份 /v3/devices 拉取所得,見下）。
  const deviceNamesRef = useRef<Map<string, string>>(new Map());
  // 目前所在的即時頁 deviceId（在看哪台就不對它彈門鈴卡——已經在現場）。
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // 全域計數 + 門鈴來電卡：人在 Events tab 上時事件直接呈現於時間軸,不計未讀;
  // 門鈴(0x8030)則不論在哪個 tab 都全螢幕彈卡（來電語意,16 §2.2),除非正在看
  // 該台即時影像。
  useEffect(() => {
    return onLiveEvent((e) => {
      // Snooze（APP#19）：該裝置在靜音窗內 → 抑制**本機顯示**（門鈴來電卡 +
      // 未讀紅點 bump）。事件仍經 EventTimeline 正常入軸/入庫（只是靜默）。
      // 注意：這只抑制本機顯示;後端推播仍會送達（伺服端推播閘是 Scott follow-up）。
      if (isSnoozed(e.deviceId)) return;
      if (isDoorbellEvent(e.ev)) {
        const onItsLive = pathnameRef.current === `/devices/${e.deviceId}/live`;
        if (!onItsLive) {
          doorbellRing.ring(e.deviceId, deviceNamesRef.current.get(e.deviceId) ?? e.deviceId, e.ringId);
        }
      }
      if (!onEventsRef.current) unreadEvents.bump();
    });
  }, []);

  // 門鈴別端已接聽（docs/panel V-14）：signaling 廣播 doorbell_answered → 門鈴卡 / 門口畫面顯示「○○ 已接聽」
  useEffect(() => signaling.on('message', (f) => doorbellAnswered.onFrame(f)), []);

  // WEB3-F3：登入即訂閱全部裝置 rooms（tab 無關；16 §5.2）。失敗靜默——badge
  // 是 best-effort 增強，devices/events 頁各自有權威載入與錯誤 UI + 重試，
  // 且其 ensureEventRooms 呼叫會在進頁時補上訂閱。
  useEffect(() => {
    let alive = true;
    apiFetch<Array<{ device_id: string; display_name?: string }>>('/v3/devices')
      .then((res) => {
        if (!alive) return;
        const devices = res.data ?? [];
        for (const d of devices) {
          if (d.display_name) {
            deviceNamesRef.current.set(d.device_id, d.display_name);
            doorbellAnswered.setName(d.device_id, d.display_name);
          }
        }
        ensureEventRooms(devices.map((d) => d.device_id));
      })
      .catch(() => {
        /* 靜默：見上 */
      });
    return () => {
      alive = false;
    };
  }, []);

  // 面板緊急求救（docs/panel 07-dashboard §3.5）：推播接上前，登入後全 App 範圍每 15 秒輪詢（背景分頁暫停）
  usePanelSosPolling();
  // 面板打來的電話 / 打給面板（08-call-family）：signaling call_ring 馬上跳 + 前景每 5 秒輪詢補漏
  usePanelCalls();
  // 行事曆 App 內通知（09 §7.1：推播只記不送 → 網頁開著時每 60 秒讀行事曆算 C4 / C3，背景分頁暫停）
  usePanelCalNoticePolling();

  // 進入 Events tab → 視為已讀（紅點清零；含停留期間新事件）。
  useEffect(() => {
    if (onEventsTab) unreadEvents.clear();
  }, [onEventsTab, unread]);

  return (
    <div className="app-shell">
      <nav className="app-nav" aria-label={t('shell.nav.ariaLabel')}>
        <div className="app-nav-brand">
          <img src={brandLogoSrc()} alt="" width={24} height={24} style={{ borderRadius: 6, objectFit: 'contain' }} />
          <span>{brandDisplayName()}</span>
        </div>
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to}>
            <NavIcon name={tab.icon} />
            {t(tab.labelKey)}
            {tab.to === '/events' && unread > 0 && (
              <span className="nav-badge" role="status" aria-label={t('shell.nav.unreadEvents', { count: unread })}>
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </NavLink>
        ))}
        <div className="app-nav-section" data-testid="nav-vms">
          <div className="app-nav-section-title">{t('nav.vms')}</div>
          {VMS_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className="app-nav-sub">
              {t(l.labelKey)}
            </NavLink>
          ))}
        </div>
      </nav>
      <main className="app-main">
        <ConnectionBanner />
        <PanelSosBanners />
        <PanelCalNotices />
        <Outlet />
      </main>
      <IncomingDoorbellCard />
      {/* 求救蓋在門鈴卡之上（z-index 較高、後渲染） */}
      <PanelSosOverlay />
      {/* 面板通話蓋在求救之上（求救自動撥的視訊要接得到） */}
      <PanelCallOverlay />
    </div>
  );
}
